import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MaxIntensity } from '@models';
import { truncate } from '@ui';
import { BadgeComponent } from '../../atoms/badge/badge.component';
import { ActionCardIconComponent } from '../../atoms/cards/action-card/action-card-icon.component';
import { BaseCardComponent } from '../../atoms/cards/base-card/base-card.component';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';

@Component({
  selector: 'ui-max-intensity-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    BaseCardComponent,
    ActionCardIconComponent,
    BadgeComponent,
    TranslocoDecimalPipe
  ],
  template: `
    <ng-container *transloco="let t">
      <ui-base-card
        [class]="'justify-between !items-start gap-y-3 '"
        [reverse]="true"
      >
        <ui-action-card-icon icon class="{{ iconClass() }}" [icon]="icon()" />
        <ng-container content>
          <div class="flex flex-col gap-y-3">
            <div class="flex gap-x-11">
              @for (item of alarmsItems(); track $index) {
                <div [class]="'flex flex-col'">
                  <span class="text-xs text-gray-400 font-medium">
                    {{ item.label }}
                  </span>
                  <span class="text-2xl text-purple">
                    {{ item?.count ? (item.count | translocoDecimal:{ minimumFractionDigits: 2, maximumFractionDigits: 2 , useGrouping: false }) + '%' : '--' }}
                  </span>
                </div>
              }
            </div>
            <h1 class="text-md">
              {{ t(label()) }}
            </h1>
          </div>
        </ng-container>
        <ng-container footer>
          <div class="flex w-full gap-x-1">
            @if (truncatedRsabtName()) {
              <ui-badge
                [class]="truncatedTsabtName() ? 'basis-1/2 cursor-pointer' : 'w-full cursor-pointer'"
                [icon]="'unordered-list'"
                (click)="handleRsabtClick()"
                [tooltipBody]="rsabtName()"
              >
                {{ truncatedRsabtName() }}
              </ui-badge>
            }
            @if (truncatedTsabtName()) {
              <ui-badge
                [class]="truncatedRsabtName() ? 'basis-1/2 cursor-pointer' : 'w-full cursor-pointer'"
                [icon]="'unordered-list'"
                (click)="handleTsabtClick()"
                [tooltipBody]="tsabtName()"
              >
                {{ truncatedTsabtName() }}
              </ui-badge>
            }
          </div>
        </ng-container>
      </ui-base-card>
    </ng-container>
  `,
})
export class MaxIntensityCardComponent {
  protected readonly TRUNCATE_MAX_CHARS: number = 30;
  private translocoService = inject(TranslocoService);

  maxIntensityData = input<MaxIntensity | null>(null);

  transformerCount = computed(() => {
    const data = this.maxIntensityData();
    if (!data?.rsabt?.rsabtMaxIntensity || !data?.rsabt?.secondaryNominalIntensity) return 0;
    return (((data.rsabt.rsabtMaxIntensity / data.rsabt.secondaryNominalIntensity) * 100).toFixed(2));
  });

  lineCount = computed(() => {
    const data = this.maxIntensityData();
    if (!data?.tsabt?.tsabtMaxIntensity || !data?.tsabt?.secondaryNominalIntensity) return 0;
    return (((data.tsabt.tsabtMaxIntensity / data.tsabt.secondaryNominalIntensity) * 100).toFixed(2));
  });

  rsabtName = computed(() => this.maxIntensityData()?.rsabt?.transformerName ?? '');
  tsabtName = computed(() => this.maxIntensityData()?.tsabt?.lineName ?? '');

  truncatedRsabtName = computed(() => {
    const part1 = this.maxIntensityData()?.rsabt?.transformerId || '';
    const part2 = this.rsabtName() || '';
    const name = !part1 && !part2 ? '---' : `${part1} - ${part2}`;
    return name ? truncate(name, this.TRUNCATE_MAX_CHARS) : '';
  });
  truncatedTsabtName = computed(() => {
    const part1 = this.maxIntensityData()?.tsabt?.lineId || '';
    const part2 = this.tsabtName() || '';
    const name = !part1 && !part2 ? '---' : `${part1} - ${part2}`;
    return name ? truncate(name, this.TRUNCATE_MAX_CHARS) : '';
  });

  alarmsLinkClick = output<void>();
  sendRsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
  }>();
  sendTsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
  }>();

  transformerLabel = toSignal(
    this.translocoService.selectTranslate('INFRAESTRUCTURE.TRANSFORMER'),
  );
  lineLabel = toSignal(
    this.translocoService.selectTranslate('INFRAESTRUCTURE.LINE'),
  );

  iconClass = signal('text-purple bg-purple-light shadow-custom-purple');
  icon = signal<string>('icons:transformer');
  label = signal<string>('DISTRIBUTION_NETWORK.MAX_INTENSITY');

  alarmsItems = computed(() => [
    {
      label: this.transformerLabel(),
      count: this.transformerCount(),
    },
    {
      label: this.lineLabel(),
      count: this.lineCount(),
    },
  ]);

  handleAlarmsLinkClick() {
    this.alarmsLinkClick.emit();
  }

  handleRsabtClick() {
    const data = this.maxIntensityData();
    if (data?.rsabt) {
      this.sendRsabtClick.emit({
        distributorId: data.rsabt.distributorId,
        centerId: data.rsabt.centerId,
        rsabtId: data.rsabt.rsabtId,
      });
    }
  }

  handleTsabtClick() {
    const data = this.maxIntensityData();
    if (data?.tsabt) {
      this.sendTsabtClick.emit({
        distributorId: data.tsabt.distributorId,
        centerId: data.tsabt.centerId,
        rsabtId: data.tsabt.rsabtId,
        tsabtId: data.tsabt.tsabtId,
      });
    }
  }
}
